import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faClock, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import Table from '../Listing/Table';
import { API_BASE_URL } from '../../api';

const PLAN_LABELS = { MONTHLY: 'Mensuel', YEARLY: 'Annuel' };
const STATUS_META = {
  PAID: { label: 'Payée', color: '#059669', icon: faCircleCheck },
  PENDING: { label: 'En attente', color: '#d97706', icon: faClock },
  REJECTED: { label: 'Refusée', color: '#dc2626', icon: faCircleXmark },
};

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtAmount = (n) =>
  new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' dhs';

const columns = [
  {
    accessor: 'invoiceNumber',
    Header: 'N° Facture',
    Cell: (row) => (
      <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
        {row.invoiceNumber}
      </span>
    ),
  },
  {
    accessor: 'subscriptionPlan',
    Header: 'Plan',
    Cell: (row) => (
      <span style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', background: '#eff6ff', padding: '3px 9px', borderRadius: 99 }}>
        {PLAN_LABELS[row.subscriptionPlan] || row.subscriptionPlan}
      </span>
    ),
  },
  {
    accessor: 'period',
    Header: 'Période',
    Cell: (row) => (
      <span style={{ fontSize: 12.5, color: '#64748b' }}>
        {fmt(row.periodStart)} <span style={{ color: '#cbd5e1' }}>→</span> {fmt(row.periodEnd)}
      </span>
    ),
  },
  {
    accessor: 'amount',
    Header: 'Montant',
    className: 'text-end',
    Cell: (row) => <strong style={{ fontSize: 13.5, color: '#0f172a', whiteSpace: 'nowrap' }}>{fmtAmount(row.amount)}</strong>,
  },
  {
    accessor: 'status',
    Header: 'Statut',
    className: 'text-center',
    Cell: (row) => {
      const meta = STATUS_META[row.status] || { label: row.status, color: '#6b7280', icon: faClock };
      const tooltip = row.status === 'REJECTED' && row.rejectionReason
        ? `${meta.label} : ${row.rejectionReason}`
        : meta.label;
      return (
        <FontAwesomeIcon
          icon={meta.icon}
          style={{ fontSize: 16, color: meta.color }}
          data-tooltip-id="tooltip"
          data-tooltip-content={tooltip}
        />
      );
    },
  },
];

const PAGE_SIZE = 10;

const SubscriptionInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  const fetchPage = useCallback((p, append = false) => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/v1/subscription-invoices`, {
      params: { page: p, size: PAGE_SIZE },
      withCredentials: true,
    })
      .then(({ data }) => {
        setInvoices(prev => append ? [...prev, ...(data.content || [])] : (data.content || []));
        setHasMore(!data.last);
        setTotalElements(data.totalElements || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, true);
  }, [page, fetchPage]);

  return (
    <Table
      columns={columns}
      data={invoices}
      loading={loading}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
      totalElements={totalElements}
      rowId="id"
      searchPlaceholder="Rechercher une facture..."
      emptyMessage="Aucune facture pour l'instant"
      showExportButton={false}
      showViewsButton={false}
    />
  );
};

export default SubscriptionInvoices;
